//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { LinkWrapper } from '@components/molecules/smart-input/link-shortening/LinkWrapper';
import { insertLinkAtPosition } from '@components/molecules/smart-input/link-shortening/linkShorteningUtils';
import { IDraftDecoratorComponentProps } from '@components/molecules/smart-input/smartInputModel';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';

interface Props extends IDraftDecoratorComponentProps {
  children: React.ReactNode;
}

export const OriginalLink = ({
  blockKey,
  children,
  decoratedText,
  end,
  start,
}: Props) => {
  const smartInputContext = useSmartInputContext();
  const [isLinkShortenButtonVisible, setIsLinkShortenButtonVisible] =
    useState(false);

  const handleInsertShortenLink = (shortLink: string) => {
    return insertLinkAtPosition(shortLink, smartInputContext, {
      blockKey,
      start,
      end,
    });
  };

  return (
    <LinkWrapper
      onMouseEnter={() => setIsLinkShortenButtonVisible(true)}
      onMouseLeave={() => setIsLinkShortenButtonVisible(false)}
    >
      {children}
      {isLinkShortenButtonVisible && (
        <ShortenLinkButton
          aria-label={`Open dialog to add a short link from ${decoratedText}`}
          contentEditable={false}
          iconName='link'
          label='Shorten Link'
          onClick={() => {
            smartInputContext.linkShortening?.openModal({
              initialOriginalLinkValue: decoratedText,
              insertShortLink: handleInsertShortenLink,
              isEditingLink: false,
            });
          }}
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.ShortLinkInlineShortenButton,
          )}
        />
      )}
    </LinkWrapper>
  );
};

const ShortenLinkButton = styled(ActionButton)(
  ({ theme }) => `
  /* Position to the right horizontally and centered vertically on the last line (if the link wraps to multiple lines) */
  position: absolute;
  bottom: -${theme.spacings[1]};
  right: 0;
  transform: translateX(100%);
  
  user-select: none;
  z-index: ${theme.zindex.popover};
  
  /* Make the background of the button white, but not the background of the wrapper which is square */
  && button {
    background-color: ${theme.colors.white};

    &:active,
    &:focus,
    &:hover {
      background-color: ${theme.colors.gray1};
    }
  }

  svg {
    fill: ${theme.colors.cyan8} !important;
  }
`,
);
